// Copyright (c) 2025 Annadaneshwar Yadawad. All rights reserved.

import { useEffect, useMemo } from 'react';
import Footer from '../components/Footer';
import './Marketing.css';

const Landing = () => {
  useEffect(() => {
    const ensureMeta = (name, content) => {
      let tag = document.querySelector(`meta[name="${name}"]`);
      if (!tag) {
        tag = document.createElement('meta');
        tag.setAttribute('name', name);
        document.head.appendChild(tag);
      }
      tag.setAttribute('content', content);
    };
    ensureMeta(
      'description',
      'Servantica — the unified AI workspace for SMS, calls, voice assistants, notifications, image generation, and more. Meet SERVA, your offline voice assistant.',
    );
    ensureMeta(
      'keywords',
      'Servantica, SERVA voice assistant, SMS platform, outbound calls, notification service, AI workspace, image generation, speech to text, offline AI assistant',
    );
  }, []);

  const faqSchema = useMemo(
    () => ({
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: [
        {
          '@type': 'Question',
          name: 'What is SERVA?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: 'SERVA is Servantica\'s offline-capable AI voice assistant available at serva.servantica.in. It provides hands-free voice interaction for tasks, queries, and AI workflows.',
          },
        },
        {
          '@type': 'Question',
          name: 'How do I send SMS in Servantica?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: 'Sign in, go to Dashboard > SMS, and use the Send tab. Features are gated per email via Razorpay.',
          },
        },
        {
          '@type': 'Question',
          name: 'What notifications does Servantica support?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: 'Servantica\'s Notification Service handles SMS delivery, email notifications, birthday reminders, and real-time push alerts with full API integration.',
          },
        },
      ],
    }),
    [],
  );

  const breadcrumbSchema = useMemo(
    () => ({
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: 'Home', item: 'https://servantica.in/' },
        { '@type': 'ListItem', position: 2, name: 'SERVA', item: 'https://serva.servantica.in/' },
        { '@type': 'ListItem', position: 3, name: 'Features', item: 'https://servantica.in/features' },
      ],
    }),
    [],
  );

  const trackCta = (label) => {
    const payload = { event: 'feature_cta', label };
    if (window?.gtag) {
      window.gtag('event', 'click', payload);
    } else if (window?.dataLayer) {
      window.dataLayer.push(payload);
    }
  };

  return (
    <>
      <div className="landing-shell">
        <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(faqSchema) }} />
        <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbSchema) }} />

        <div className="landing-pattern" />
        <div className="landing-noise" />
        <div className="landing-bubbles" aria-hidden="true">
          <span className="bubble b1" />
          <span className="bubble b2" />
          <span className="bubble b3" />
          <span className="bubble b4" />
          <span className="bubble b5" />
          <span className="bubble b6" />
          <span className="bubble b7" />
          <span className="bubble b8" />
        </div>

        {/* Hero */}
        <div className="hero animate-fade">
          <div className="hero-copy">
            <span className="pill float">AI-powered workspace</span>
            <h1 className="animate-up">Your unified platform for SMS, Calls, AI, and Voice Assistants</h1>
            <p className="animate-up delay-1">
              Servantica brings communications, AI tools, and voice assistants into one workspace. Send SMS, place calls, generate images, and talk to SERVA — all from a single dashboard.
            </p>
            <div className="hero-cta animate-up delay-2">
              <a className="btn-primary" href="/login?returnTo=/dashboard" onClick={() => trackCta('hero_dashboard')}>
                Open Dashboard
              </a>
              <a className="btn-ghost" href="#serva-section" onClick={() => trackCta('hero_meet_serva')}>
                Meet SERVA
              </a>
            </div>
            <div className="hero-meta animate-up delay-3">
              <span>SMS + Calls</span>
              <span>AI Image Studio</span>
              <span>Voice Assistant</span>
              <span>Notifications</span>
            </div>
          </div>

          <div className="hero-panel animate-up delay-1">
            <div className="glass-card">
              <div className="card-head">
                <div>
                  <p className="label">Platform overview</p>
                  <h3>Everything in one place</h3>
                </div>
                <span className="tag success">Live</span>
              </div>
              <div className="stat-list">
                <div className="stat-line">
                  <span className="stat-label">SERVA Assistant</span>
                  <span className="stat-value">serva.servantica.in</span>
                </div>
                <div className="stat-line">
                  <span className="stat-label">SMS + Calls</span>
                  <span className="stat-value">Bulk & outbound</span>
                </div>
                <div className="stat-line">
                  <span className="stat-label">Notifications</span>
                  <span className="stat-value">notification.servantica.in</span>
                </div>
                <div className="stat-line">
                  <span className="stat-label">AI Studio</span>
                  <span className="stat-value">Image + Video</span>
                </div>
              </div>
              <div className="pill-row">
                <span className="pill soft">SERVA</span>
                <span className="pill soft">SMS</span>
                <span className="pill soft">Calls</span>
                <span className="pill soft">AI</span>
                <span className="pill soft">Notifications</span>
              </div>
            </div>
          </div>
        </div>

        {/* Highlight cards */}
        <section className="sections animate-up hero-highlights">
          <div className="highlight-grid priority-grid">
            <div className="highlight-card tilt">
              <div className="highlight-icon">🗣️</div>
              <h3>SERVA Voice AI</h3>
              <p>Offline-capable voice assistant at serva.servantica.in for hands-free AI interaction.</p>
            </div>
            <div className="highlight-card tilt delay-1">
              <div className="highlight-icon">📩</div>
              <h3>SMS delivery</h3>
              <p>Reliable send, schedule, and bulk messaging with delivery tracking.</p>
            </div>
            <div className="highlight-card tilt delay-2">
              <div className="highlight-icon">📞</div>
              <h3>Outbound calls</h3>
              <p>Place and track calls with notes from the same dashboard.</p>
            </div>
            <div className="highlight-card tilt delay-3">
              <div className="highlight-icon">🔔</div>
              <h3>Smart notifications</h3>
              <p>Email, SMS, and push alerts with birthday reminders and scheduling.</p>
            </div>
            <div className="highlight-card tilt delay-4">
              <div className="highlight-icon">🎨</div>
              <h3>AI image studio</h3>
              <p>Convert text prompts to stunning visuals with AI generation.</p>
            </div>
          </div>
        </section>

        {/* SERVA Voice Assistant Showcase */}
        <section id="serva-section" className="sections animate-up serva-showcase">
          <div className="section-head">
            <p className="pill serva-pill">Meet SERVA</p>
            <h2>Your AI voice assistant, online and offline</h2>
            <p className="muted">SERVA lives at serva.servantica.in — a dedicated voice assistant that works even without an internet connection.</p>
          </div>
          <div className="serva-grid">
            <div className="serva-hero-card">
              <div className="serva-orb" aria-hidden="true">
                <span className="serva-ring" />
                <span className="serva-ring r2" />
                <span className="serva-ring r3" />
                <div className="serva-core">S</div>
              </div>
              <h3>SERVA</h3>
              <p>Servantica's voice-first AI assistant</p>
              <a
                className="serva-launch-btn"
                href="https://serva.servantica.in"
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => trackCta('serva_launch')}
              >
                Launch SERVA
                <span className="serva-arrow">→</span>
              </a>
            </div>
            <div className="serva-features">
              <div className="serva-feature-item">
                <div className="serva-feat-icon">🎙️</div>
                <div>
                  <strong>Wake word activation</strong>
                  <p>Say the wake word and SERVA starts listening instantly — no buttons needed.</p>
                </div>
              </div>
              <div className="serva-feature-item">
                <div className="serva-feat-icon">📡</div>
                <div>
                  <strong>Offline capable</strong>
                  <p>SERVA works without internet using on-device processing for core voice tasks.</p>
                </div>
              </div>
              <div className="serva-feature-item">
                <div className="serva-feat-icon">🧠</div>
                <div>
                  <strong>AI-powered responses</strong>
                  <p>Natural language understanding with context-aware answers and task routing.</p>
                </div>
              </div>
              <div className="serva-feature-item">
                <div className="serva-feat-icon">🔗</div>
                <div>
                  <strong>Servantica integration</strong>
                  <p>Connected to SMS, calls, notifications, and the full Servantica workspace.</p>
                </div>
              </div>
            </div>
          </div>
          <div className="serva-platforms">
            <div className="serva-platform-card">
              <span className="serva-plat-icon">🌐</span>
              <strong>Web</strong>
              <p>serva.servantica.in</p>
              <span className="pill soft">Live</span>
            </div>
            <div className="serva-platform-card">
              <span className="serva-plat-icon">📱</span>
              <strong>Android</strong>
              <p>ARVA offline assistant</p>
              <span className="pill soft soon">Coming soon</span>
            </div>
            <div className="serva-platform-card">
              <span className="serva-plat-icon">🖥️</span>
              <strong>Desktop</strong>
              <p>Voice assistant for PC</p>
              <span className="pill soft soon">Planned</span>
            </div>
          </div>
        </section>

        {/* Notification Service Showcase */}
        <section id="notification-section" className="sections animate-up notif-showcase">
          <div className="section-head">
            <p className="pill notif-pill">Notification Service</p>
            <h2>Reach your users through every channel</h2>
            <p className="muted">
              Servantica's notification engine at{' '}
              <a href="https://notification.servantica.in/" target="_blank" rel="noopener noreferrer" className="notif-link">
                notification.servantica.in
              </a>
              {' '}delivers SMS, email, and push alerts with smart scheduling and full API access.
            </p>
          </div>
          <div className="notif-hero-banner">
            <div className="notif-banner-content">
              <div className="notif-banner-icon">🔔</div>
              <div>
                <h3>notification.servantica.in</h3>
                <p>API dashboard, Swagger docs, health monitoring, and real-time notification management</p>
              </div>
            </div>
            <a
              className="notif-banner-btn"
              href="https://notification.servantica.in/"
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => trackCta('notif_open_dashboard')}
            >
              Open Notification Dashboard
              <span>→</span>
            </a>
          </div>
          <div className="notif-grid">
            <div className="notif-card">
              <div className="notif-card-icon">📧</div>
              <h3>Email notifications</h3>
              <p>SMTP-powered email delivery with custom templates, HTML support, and delivery tracking through Hostinger.</p>
            </div>
            <div className="notif-card">
              <div className="notif-card-icon">💬</div>
              <h3>SMS messaging</h3>
              <p>Twilio-backed SMS delivery with bulk send, scheduling, and per-message status tracking stored in MongoDB.</p>
            </div>
            <div className="notif-card">
              <div className="notif-card-icon">🔔</div>
              <h3>Push alerts</h3>
              <p>Real-time admin push notifications, unread badges, and toast alerts that stay out of the way.</p>
            </div>
            <div className="notif-card">
              <div className="notif-card-icon">🎂</div>
              <h3>Birthday reminders</h3>
              <p>Automated birthday scheduler runs daily at 10 AM, sending personalized wishes to your contacts.</p>
            </div>
            <div className="notif-card">
              <div className="notif-card-icon">🔐</div>
              <h3>Secure API</h3>
              <p>JWT-authenticated REST API with Auth0 and Keycloak support for enterprise-grade access control.</p>
            </div>
            <div className="notif-card">
              <div className="notif-card-icon">📊</div>
              <h3>Monitoring</h3>
              <p>Prometheus metrics, health endpoints, and Swagger UI for complete observability and API documentation.</p>
            </div>
          </div>
          <div className="notif-cta-row">
            <a className="btn-primary" href="https://notification.servantica.in/" target="_blank" rel="noopener noreferrer" onClick={() => trackCta('notif_open_service')}>
              Open notification.servantica.in
            </a>
            <a className="btn-primary" href="/login?returnTo=/dashboard/sms?mode=send" onClick={() => trackCta('notif_send_sms')}>
              Send SMS now
            </a>
            <a className="btn-ghost" href="/features" onClick={() => trackCta('notif_learn_more')}>
              Explore all features
            </a>
          </div>
        </section>

        {/* AI Features */}
        <section id="ai-media" className="sections animate-up hero-features">
          <div className="section-head">
            <p className="pill glow">AI Studio</p>
            <h2>Create stunning media with AI</h2>
            <p className="muted">Transform your ideas into images and videos with cutting-edge AI technology</p>
          </div>
          <div className="ai-features-grid">
            <a
              href="/login?returnTo=/image-generation"
              className="ai-feature-card image-gen"
              onClick={() => trackCta('ai_image_generation')}
            >
              <div className="ai-card-glow"></div>
              <div className="ai-card-content">
                <div className="ai-icon">🎨</div>
                <h3>AI Image Generation</h3>
                <p>Turn text prompts into beautiful, unique images in seconds</p>
                <span className="ai-badge available">Available Now</span>
                <div className="ai-card-arrow">→</div>
              </div>
            </a>
            <a href="/video-generation" className="ai-feature-card video-gen" onClick={() => trackCta('ai_video_generation')}>
              <div className="ai-card-glow"></div>
              <div className="ai-card-content">
                <div className="ai-icon">🎥</div>
                <h3>AI Video Generation</h3>
                <p>Create engaging videos from simple text descriptions</p>
                <span className="ai-badge coming-soon">Coming Soon</span>
                <div className="ai-card-arrow">→</div>
              </div>
            </a>
          </div>
        </section>

        {/* Quick start CTAs */}
        <section className="sections animate-up feature-cta" id="start-now">
          <div className="section-head">
            <p className="pill">Start now</p>
            <h2>Jump into any tool instantly</h2>
            <p className="muted">Direct links open the right page after you sign in.</p>
          </div>
          <div className="feature-cta-grid">
            <a className="feature-cta-card" href="https://serva.servantica.in" target="_blank" rel="noopener noreferrer" onClick={() => trackCta('start_serva')}>
              <div className="cta-icon cta-icon-serva">🗣️</div>
              <div>
                <h3>Launch SERVA</h3>
                <p>Open the AI voice assistant at serva.servantica.in.</p>
              </div>
              <span className="cta-arrow">→</span>
            </a>
            <a className="feature-cta-card" href="/login?returnTo=/dashboard/sms?mode=send" onClick={() => trackCta('start_sms')}>
              <div className="cta-icon">📩</div>
              <div>
                <h3>Send SMS</h3>
                <p>Go straight to the SMS composer to send or schedule messages.</p>
              </div>
              <span className="cta-arrow">→</span>
            </a>
            <a className="feature-cta-card" href="/login?returnTo=/dashboard/calls?mode=call" onClick={() => trackCta('start_calls')}>
              <div className="cta-icon">📞</div>
              <div>
                <h3>Place Calls</h3>
                <p>Open outbound calls with notes and tracking.</p>
              </div>
              <span className="cta-arrow">→</span>
            </a>
            <a className="feature-cta-card" href="/login?returnTo=/image-generation" onClick={() => trackCta('start_image_generation')}>
              <div className="cta-icon">🎨</div>
              <div>
                <h3>Image Studio</h3>
                <p>Launch the AI studio to convert prompts to images.</p>
              </div>
              <span className="cta-arrow">→</span>
            </a>
          </div>
        </section>

        {/* Capabilities grid */}
        <section id="capabilities" className="sections animate-up">
          <div className="section-head">
            <p className="pill">What you get</p>
            <h2>Everything in one clean workspace</h2>
            <p className="muted">No clutter. Just the tools you need to communicate and create.</p>
          </div>
          <div className="grid-3 clean-grid">
            <div className="card feature-card cap-1">
              <div className="icon-circle">🗣️</div>
              <h3>SERVA Assistant</h3>
              <p>AI voice assistant at serva.servantica.in with wake-word activation and offline capability.</p>
              <a className="feature-cta-mini" href="https://serva.servantica.in" target="_blank" rel="noopener noreferrer" onClick={() => trackCta('cta_serva')}>
                Try SERVA
              </a>
            </div>
            <div className="card feature-card cap-2">
              <div className="icon-circle">📩</div>
              <h3>SMS + Calls</h3>
              <p>Bulk SMS, outbound call automation, and scheduled campaigns with secure token authentication.</p>
              <a className="feature-cta-mini" href="/login?returnTo=/dashboard/sms?mode=send" onClick={() => trackCta('cta_sms_feature_card')}>
                Try in dashboard
              </a>
            </div>
            <div className="card feature-card cap-3">
              <div className="icon-circle">🔔</div>
              <h3>Notifications</h3>
              <p>Email, SMS, and push notifications with birthday reminders and real-time delivery.</p>
            </div>
            <div className="card feature-card cap-4">
              <div className="icon-circle">🎨</div>
              <h3>Image Generation</h3>
              <p>Convert prompts to visuals with the AI image studio.</p>
              <span className="pill soft">Available</span>
              <a className="feature-cta-mini" href="/login?returnTo=/image-generation" onClick={() => trackCta('cta_image_feature_card')}>
                Generate now
              </a>
            </div>
            <div className="card feature-card cap-5">
              <div className="icon-circle">🖼️</div>
              <h3>Speech + Vision</h3>
              <p>Speech-to-text, text-to-speech, face detection, and image analysis.</p>
              <a className="feature-cta-mini" href="/login?returnTo=/dashboard/speech-to-text" onClick={() => trackCta('cta_speech_feature_card')}>
                Try speech
              </a>
            </div>
            <div className="card feature-card cap-6">
              <div className="icon-circle">📂</div>
              <h3>File storage</h3>
              <p>Upload, list, preview, download, and manage files alongside messaging.</p>
            </div>
            <div className="card feature-card cap-7">
              <div className="icon-circle">🎥</div>
              <h3>Video Generation</h3>
              <p>Create motion from text prompts — arriving soon.</p>
              <span className="pill soft soon">Coming soon</span>
            </div>
            <div className="card feature-card cap-8">
              <div className="icon-circle">💳</div>
              <h3>Billing guardrails</h3>
              <p>Email-aware paywall hooks plus quick unlock flow for returning subscribers.</p>
            </div>
          </div>
        </section>

        {/* How it works */}
        <section className="sections animate-up how-it-works" aria-labelledby="how-it-works-heading">
          <div className="section-head">
            <p className="pill">How it works</p>
            <h2 id="how-it-works-heading">Get started in 3 simple steps</h2>
            <p className="muted">Sign in, unlock features, and start using SMS, Calls, SERVA, and AI tools.</p>
          </div>
          <div className="how-steps">
            <div className="how-step">
              <span className="step-badge">1</span>
              <h3>Sign in</h3>
              <p>Use secure login to access your Servantica workspace instantly.</p>
            </div>
            <div className="how-step">
              <span className="step-badge">2</span>
              <h3>Unlock features</h3>
              <p>One-time Razorpay unlock for SMS, outbound calls, and AI tools.</p>
            </div>
            <div className="how-step">
              <span className="step-badge">3</span>
              <h3>Launch tools</h3>
              <p>Open SMS, Calls, SERVA, Image Studio, or any tool directly from the dashboard.</p>
            </div>
          </div>
        </section>

        {/* Trust strip */}
        <section className="sections animate-up trust-strip" aria-label="Security and billing">
          <div className="trust-items">
            <div className="trust-item">Secure JWT authentication</div>
            <div className="trust-item">Razorpay payments</div>
            <div className="trust-item">TLS encrypted transport</div>
            <div className="trust-item">Email-aware access control</div>
          </div>
          <div className="micro-card">
            <h3>What you get for ₹5</h3>
            <ul>
              <li>Unlock SMS, Calls, and AI tools</li>
              <li>SERVA voice assistant access</li>
              <li>Full notification service integration</li>
            </ul>
          </div>
        </section>

        {/* Unlock CTA */}
        <section className="sections animate-up" aria-label="Unlock all features">
          <div className="section-head">
            <p className="pill glow">₹5 unlock</p>
            <h2>One button, every feature</h2>
            <p className="muted">
              The ₹5 unlock keeps SMS delivery stable, pays for outbound call minutes, and covers GPU time for image generation.
              Your email stays linked so you can re-login without paying again.
            </p>
          </div>
          <div className="hero-cta">
            <a className="btn-primary" href="/pricing" onClick={() => trackCta('unlock_all_features')}>
              Unlock all features for ₹5
            </a>
            <a className="btn-ghost" href="#capabilities" onClick={() => trackCta('view_all_features')}>
              View everything included
            </a>
          </div>
        </section>
      </div>

      <Footer />
    </>
  );
};

export default Landing;
